import { useCallback, useEffect, useState } from 'react'
import { Pager } from '@/components/components/Pager'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { TableStateContext, TableStateContextData } from '@/utils/TableStateContext'
import { SortDescriptor } from '@progress/kendo-data-query'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query/dist/npm/filtering/filter-descriptor.interface'
import { GridColumn as Column, Grid, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { PageData } from 'api/Pagination/Types'
import { AccountGroupDto } from 'api/Trading-Accounts/Types/AccountGroupDto'
import { FilterType } from 'components/Filter'
import { getAccountGroupFilterFields } from 'components/Filter/accountGroupFields'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import { GridFilterActionState, resetGridFilters, setGridFilters } from 'store/grid'
import { fetchCountriesAction, fetchTickmillCompaniesAction } from 'store/identity/identity.actions'
import {
  fetchAllServerAction,
  fetchBooksTypeAction,
  fetchPlatformTypesAction,
  fetchTradingAccountClassificationTypeAction,
  fetchTradingAccountGroupTypeAction,
  fetchTradingAccountStatusAction,
  fetchTradingAccountTypeAction,
} from 'store/trading-accounts/trading-accounts.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'

import accountGroupColumn from './accountGroupColumn'
import { normalizeAccountGroupsSearchQuery, normalizeAccountGroupsSortQuery } from './normalizer'

const useMapStateToProps = () => ({
  servers: useAppSelector((state) => state.tradingAccounts.allServers),
  bookType: useAppSelector((state) => state.tradingAccounts.bookType),
  tradingAccountStatus: useAppSelector((state) => state.tradingAccounts.tradingAccountStatus),
  tradingAccountType: useAppSelector((state) => state.tradingAccounts.tradingAccountType),
  tradingAccountGroupType: useAppSelector((state) => state.tradingAccounts.tradingAccountGroupType),
  tradingAccountClassificationType: useAppSelector(
    (state) => state.tradingAccounts.tradingAccountClassificationType
  ),
  countries: useAppSelector((state) => state.identity.countries),
  platformTypes: useAppSelector((state) => state.tradingAccounts.platformTypes),
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
  gridState: useAppSelector((state) => state.grid),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  return {
    fetchAllServers: async () => dispatch(fetchAllServerAction(tradingAccountsApiClient)),
    fetchBookTypes: async () => dispatch(fetchBooksTypeAction(tradingAccountsApiClient)),
    fetchTradingAccountStatus: async () =>
      dispatch(fetchTradingAccountStatusAction(tradingAccountsApiClient)),
    fetchCountries: async () => dispatch(fetchCountriesAction(identityApiClient)),
    fetchTradingAccountType: async () =>
      dispatch(fetchTradingAccountTypeAction(tradingAccountsApiClient)),
    fetchTradingAccountGroupType: async () =>
      dispatch(fetchTradingAccountGroupTypeAction(tradingAccountsApiClient)),
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
    fetchClassification: async () =>
      dispatch(fetchTradingAccountClassificationTypeAction(tradingAccountsApiClient)),
    fetchPlatformTypes: async () => dispatch(fetchPlatformTypesAction(tradingAccountsApiClient)),
    fetchTickmillCompanies: async () => dispatch(fetchTickmillCompaniesAction(identityApiClient)),
  }
}

const AccountGroups = (): JSX.Element => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })

  const [accountGroups, setAccountGroups] = useState<AccountGroupDto[]>([])
  const crumbs = [{ title: 'Account Groups', link: links.recordsAccountGroups.url }]

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const {
    countries,
    tradingAccountGroupType,
    tradingAccountStatus,
    tradingAccountType,
    tradingAccountClassificationType,
    bookType,
    servers,
    platformTypes,
    tickmillCompanies,
  } = useMapStateToProps()
  const {
    fetchAllServers,
    fetchBookTypes,
    fetchTradingAccountStatus,
    fetchCountries,
    fetchTradingAccountType,
    fetchTradingAccountGroupType,
    fetchClassification,
    fetchPlatformTypes,
    fetchTickmillCompanies,
  } = useMapDispatchToProps()

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (gridFilterState: GridFilterActionState): Promise<PageData<AccountGroupDto[]>> => {
          const searchQuery = getQuery(gridFilterState.filter)
          const sortQuery = getSort(gridFilterState.sort)
          const response = await tradingAccountsApiClient.getAccountGroups({
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.pageSize,
            skipCount: tableData?.skipCount !== false,
          })

          if (isEmpty(servers)) await fetchAllServers()
          if (isEmpty(bookType)) await fetchBookTypes()
          if (isEmpty(tradingAccountStatus)) await fetchTradingAccountStatus()
          if (isEmpty(countries)) await fetchCountries()
          if (isEmpty(tradingAccountType)) await fetchTradingAccountType()
          if (isEmpty(tradingAccountGroupType)) await fetchTradingAccountGroupType()
          if (isEmpty(tradingAccountClassificationType)) await fetchClassification()
          if (isEmpty(platformTypes)) await fetchPlatformTypes()
          if (isEmpty(tickmillCompanies)) await fetchTickmillCompanies()
          if (response?.items) {
            setAccountGroups(response.items)
          }
          setTableData({ ...tableData, meta: response })
          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<AccountGroupDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )
    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(normalizeAccountGroupsSearchQuery(filter ?? gridState.filter)) ?? null
    },
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(normalizeAccountGroupsSortQuery(sortDescriptors ?? gridState.sort))
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const searchQuery = params?.query !== undefined ? params.query : getQuery()
      const sortQuery = getSort(gridState.sort)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await tradingAccountsApiClient.getAccountGroups({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setAccountGroups(response.items.map((x) => Object.assign({ selected: false }, x)))
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getQuery,
      getSort,
      gridActions,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ]
  )

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await tradingAccountsApiClient.getAccountGroups({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    if (response?.items) {
      setAccountGroups(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }
  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const response = await tradingAccountsApiClient.getAccountGroups({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    if (response?.items) {
      setAccountGroups(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await tradingAccountsApiClient.getAccountGroups({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setAccountGroups(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleGridPageChange = async (pageSize: number) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = pageSize
    gridActions.setGridFilters({
      pageIndex,
    })
    const response = await tradingAccountsApiClient.getAccountGroups({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setAccountGroups(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <>
      <Breadcrumbs crumbs={crumbs} />
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.ACCOUNT_GROUP}
        filterFields={getAccountGroupFilterFields()}
        getQuery={getQuery}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport data={accountGroups}>
          <Grid
            {...gridState}
            resizable
            data={accountGroups}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            onSortChange={handleSortChange}
            pageable={true}
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
            style={{ zIndex: 0 }}
          >
            {accountGroupColumn
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column key={idx + 2} {...column} />
              ))}
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
    </>
  )
}

export default AccountGroups
